import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface SurfaceBlurParams {
  radius: number;
  threshold: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const SurfaceBlur: Filter<SurfaceBlurParams> = {
  id: "surface-blur",
  displayName: "Surface Blur",
  defaultParams: { radius: 5, threshold: 15 },

  apply(input: PixelBuffer, params: SurfaceBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0 || params.radius < 1) {
      dst.set(src);
      return out;
    }

    const r = Math.round(params.radius);
    const threshold = params.threshold;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const cR = src[o]!, cG = src[o + 1]!, cB = src[o + 2]!;
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, weightTotal = 0;

        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx));
            const sy = Math.max(0, Math.min(h - 1, y + dy));
            const si = (sy * w + sx) * 4;
            const diff = Math.abs(src[si]! - cR) + Math.abs(src[si + 1]! - cG) + Math.abs(src[si + 2]! - cB);
            const weight = Math.max(0, 1 - diff / (3 * threshold));
            sumR += src[si]! * weight;
            sumG += src[si + 1]! * weight;
            sumB += src[si + 2]! * weight;
            sumA += src[si + 3]! * weight;
            weightTotal += weight;
          }
        }

        if (weightTotal > 0) {
          dst[o] = clamp8(Math.round(sumR / weightTotal));
          dst[o + 1] = clamp8(Math.round(sumG / weightTotal));
          dst[o + 2] = clamp8(Math.round(sumB / weightTotal));
          dst[o + 3] = clamp8(Math.round(sumA / weightTotal));
        } else {
          dst[o] = cR; dst[o + 1] = cG; dst[o + 2] = cB; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
